import React, { useState, useEffect, useRef } from "react";
import "./GPTUsers.scss";
import { getAllGPTUsers, blockUser } from "../../api/apiServiceFetch";
import ConfirmPopup from "../../components/ConfirmPopup";
import PopupMessage from "../../components/PopupMessage";
import PopupGift from "../../components/PopupGift";
import {
	BLOCK_REASON_REQUIRED_TEXT,
	isBlockReasonValid,
	normalizeBlockReason,
} from "../../components/helper";
import { format, addDays, startOfMonth } from "date-fns";

const ITEMS_PER_PAGE = 10;
const GPTUsers = () => {
	const textareaRef = useRef(null);
	const [users, setUsers] = useState([]);
	const [filtered, setFiltered] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [expandedUserId, setExpandedUserId] = useState(null);
	const [filterTerm, setFilterTerm] = useState("");
	const [hasActiveOrderFilter, setHasActiveOrderFilter] = useState(null);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [activeOrderCount, setActiveOrderCount] = useState(0); // New state for active order count
	const [reason, setReason] = useState("");
	const [isPopupMessageOpen, setIsPopupMessageOpen] = useState(false);
	const [isGiftPopupOpen, setIsGiftPopupOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const [showBlock, setShowBlock] = useState(false);

	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
			textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
		}
	};

	useEffect(() => {
		autoResizeTextarea();
	}, [reason]);

	const fetchUsers = async () => {
		try {
			const newUsers = await getAllGPTUsers(
				pageNumber,
				ITEMS_PER_PAGE,
				filterTerm,
				hasActiveOrderFilter
			);
			if (newUsers.data.length === 0) {
				setUsers([]);
				setFiltered([]);
				setTotalPages(1);
				setTotalCount(0);
			} else {
				setUsers(newUsers.data);
				setFiltered(newUsers.data);
				const totalPages = Math.ceil(newUsers.totalCount / ITEMS_PER_PAGE);
				setTotalPages(totalPages < 1 ? 1 : totalPages);
				setTotalCount(newUsers.totalCount);
				setActiveOrderCount(newUsers.activeOrderCount); // Set the new count
			}
		} catch (error) {
			setUsers([]);
			setFiltered([]);
			setTotalPages(1);
			setTotalCount(0);
			console.error("Error fetching users by IDs:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, [pageNumber, filterTerm, hasActiveOrderFilter]);

	const handleExpandClick = (userId) => {
		const user = users.find((user) => user.userId === userId);
		setExpandedUserId(expandedUserId === userId ? null : userId);
	};

	const toggleBlockModal = () => {
		setShowBlock(!showBlock);
	};

	const applyBlock = async () => {
		// Причина обязательна: блокировку без неё потом не объяснить, и бэкенд
		// её отклоняет.
		const blockReason = normalizeBlockReason(reason);
		if (!blockReason) {
			alert(BLOCK_REASON_REQUIRED_TEXT);
			return;
		}
		const data = {
			userId: expandedUserId,
			reason: blockReason,
		};
		await blockUser(data);
		setShowBlock(false);
		setFiltered((prevUsers) =>
			prevUsers.filter((user) => user.userId !== expandedUserId)
		);
	};

	if (loading) {
		return <div>Загрузка...</div>;
	}

	const closeMessagePopup = () => {
		setIsPopupMessageOpen(false);
	};

	const closeConfirmGiftPopup = () => {
		setIsGiftPopupOpen(false);
	};

	const handleMasage = () => {
		setIsPopupMessageOpen(true);
	};

	const gifthandler = () => {
		setIsGiftPopupOpen(true);
	};

	const formatDateTime = (date) =>
		format(new Date(date), "dd.MM.yyyy 'время:' HH:mm");
	const formatDate = (date) => format(new Date(date), "dd.MM.yyyy");

	const handleInputChange = (event) => {
		setReason(event.target.value);
	};

	const handlePreviousPage = () => {
		if (pageNumber > 1) {
			setPageNumber((prev) => prev - 1);
		}
	};

	const handleNextPage = () => {
		if (pageNumber < totalPages) {
			setPageNumber((prev) => prev + 1);
		}
	};

	const handleSearchChange = (e) => {
		setFilterTerm(e.target.value);
		setPageNumber(1);
	};

	const handleActiveOrderFilterChange = (e) => {
		const checked = e.target.checked;
		setHasActiveOrderFilter(checked ? true : null);
		setPageNumber(1);
	};

	return (
		<div className="GPTUsers">
			<PopupGift
				isOpen={isGiftPopupOpen}
				onClose={closeConfirmGiftPopup}
				userId={expandedUserId}
			/>
			<PopupMessage
				isOpen={isPopupMessageOpen}
				onClose={closeMessagePopup}
				type={100}
				sex={-1}
			/>

			<div className="filter-button">
				<button onClick={handleMasage}>Написать всем</button>
			</div>

			{/* Add user counts display */}
			<div className="user-stats">
				{hasActiveOrderFilter ? (
					<p>Всего пользователей: {totalCount}</p>
				) : (
					<p>
						Всего пользователей: {totalCount}, с активной подпиской:{" "}
						{activeOrderCount}
					</p>
				)}
				<label className="active-order-filter">
					<input
						type="checkbox"
						checked={hasActiveOrderFilter === true}
						onChange={handleActiveOrderFilterChange}
					/>
					Только с активной подпиской
				</label>
			</div>

			<div className="search-bar">
				<input
					type="text"
					placeholder="Поиск по профилям..."
					value={filterTerm}
					onChange={handleSearchChange}
					className="search-input"
				/>
			</div>
			{showBlock && (
				<div className="filter-modal">
					<div className="filter-modal-content">
						<h3>Заблокировать пользователя?</h3>
						<p>Напишите причину</p>
						<textarea
							id="message"
							className={"textArea"}
							name="message"
							value={reason}
							onChange={handleInputChange}
							ref={textareaRef}
						/>
						{!isBlockReasonValid(reason) && (
							<p className="block-reason-hint">
								{BLOCK_REASON_REQUIRED_TEXT}
							</p>
						)}

						<button
							onClick={applyBlock}
							disabled={!isBlockReasonValid(reason)}
						>
							Заблокировать
						</button>
						<button onClick={toggleBlockModal}>Отмена</button>
					</div>
				</div>
			)}
			{filtered.length === 0 ? <div>Нет профилей</div> : ""}
			{filtered.map((user) => (
				<div key={user.userId} className="profile-card">
					<div
						className="profile-summary"
						onClick={() => handleExpandClick(user.userId)}
					>
						<h2>
							{user.fullName} ({user.userId})
						</h2>
						<p> Никнейм: {user.userName}</p>
					</div>
					{expandedUserId === user.userId && (
						<div className="profile-details">
							<div className="details-info">
								<div className="info-text">
									<p>Дата Регистрации: {formatDateTime(user.cDate)}</p>
									<p>
										Дата Последнего входа: {formatDateTime(user.lastLoginDate)}
									</p>
									<p>Количество запросов: {user.callCount}</p>
									<p>Есть подиска: {user.hasActiveOrder ? "ДА" : "НЕТ"}</p>
									{user.hasActiveOrder && (
										<p>
											Подписка активна до: {formatDateTime(user.orderEndDate)}
										</p>
									)}
								</div>
							</div>
							{/* <button className='gift' onClick={gifthandler} disabled={0}>Подарить 4o</button> */}
						</div>
					)}
				</div>
			))}
			<div className="pagination">
				<button onClick={handlePreviousPage} disabled={pageNumber === 1}>
					Previous
				</button>
				<span>
					Page {pageNumber} of {totalPages}
				</span>
				<button onClick={handleNextPage} disabled={pageNumber === totalPages}>
					Next
				</button>
			</div>
		</div>
	);
};

export default GPTUsers;
